/**
 * NodeRadar Database Backup and Restore Module.
 *
 * Provides full database dumps and session-specific portable JSON import/export
 * capabilities across desktop, tablet, and smartphone browsers.
 */

import { getDb, StoredPacket, StoredNode, StoredTrace, RadarSession } from './db';
import { downloadFile } from './exporters';

export interface NodeRadarBackupData {
  format: 'NodeRadar_Backup';
  version: 1;
  exportedAt: number;
  exportedAtIso: string;
  scope: 'full' | 'session';
  sessionId?: string;
  sessions: RadarSession[];
  packets: StoredPacket[];
  nodes: StoredNode[];
  traces?: StoredTrace[];
}

export interface ImportBackupResult {
  sessionsCount: number;
  packetsCount: number;
  nodesCount: number;
  tracesCount: number;
  activeSessionId?: string;
}

/**
 * Exports either a specific session or the full NodeRadar database as a portable JSON backup.
 *
 * Why: Enables transferring telemetry logs between mobile devices and analysis PCs,
 * and safeguards database state against accidental browser data wipes.
 *
 * @param sessionId - Optional session ID to export only a single session
 */
export async function exportDatabaseBackup(sessionId?: string): Promise<void> {
  const db = await getDb();

  let sessions: RadarSession[] = [];
  let packets: StoredPacket[] = [];
  let nodes: StoredNode[] = [];
  let traces: StoredTrace[] = [];
  let scope: 'full' | 'session' = 'full';
  let defaultFilename = `noderadar_backup_${Date.now()}.json`;

  if (sessionId) {
    scope = 'session';
    const session = await db.get('sessions', sessionId);
    if (session) {
      sessions = [session];
      const safeName = session.name.replace(/[^a-zA-Z0-9_\-]/g, '_');
      defaultFilename = `noderadar_session_${safeName}_${Date.now()}.json`;
    }

    packets = await db.getAllFromIndex('packets', 'by-session', sessionId);
    traces = await db.getAllFromIndex('traces', 'by-session', sessionId);

    // Identify all unique nodes involved in this session's telemetry
    const involvedNodeNums = new Set<number>();
    for (const p of packets) {
      if (p.from) involvedNodeNums.add(p.from);
      if (p.to && p.to !== 0xffffffff) involvedNodeNums.add(p.to);
      if (p.relayNode) involvedNodeNums.add(p.relayNode);
    }

    for (const num of involvedNodeNums) {
      const node = await db.get('nodes', num);
      if (node) {
        nodes.push(node);
      }
    }
  } else {
    sessions = await db.getAll('sessions');
    packets = await db.getAll('packets');
    nodes = await db.getAll('nodes');
    traces = await db.getAll('traces');
  }

  const backupData: NodeRadarBackupData = {
    format: 'NodeRadar_Backup',
    version: 1,
    exportedAt: Date.now(),
    exportedAtIso: new Date().toISOString(),
    scope,
    sessionId,
    sessions,
    packets,
    nodes,
    traces
  };

  downloadFile(JSON.stringify(backupData, null, 2), defaultFilename, 'application/json');
}

/**
 * Validates and restores a NodeRadar JSON backup file into IndexedDB.
 *
 * Why: Restores previously recorded tracks and stations, intelligently merging
 * node metadata and preventing session ID collisions.
 *
 * @param jsonContent - Raw JSON string containing the exported NodeRadar backup
 * @returns Promise resolving to an import summary
 */
export async function importDatabaseBackup(jsonContent: string): Promise<ImportBackupResult> {
  let parsed: unknown;
  try {
    parsed = JSON.parse(jsonContent);
  } catch (err) {
    throw new Error(`Ungültiges JSON-Format: ${(err as Error).message}`);
  }

  const backup = parsed as NodeRadarBackupData;
  if (!backup || backup.format !== 'NodeRadar_Backup' || !Array.isArray(backup.packets)) {
    throw new Error('Ungültige Backup-Datei: Format "NodeRadar_Backup" nicht erkannt.');
  }

  const db = await getDb();
  const sessionIdMap = new Map<string, string>();
  let importedSessions = 0;
  let lastSessionId: string | undefined;

  // 1. Restore Sessions
  if (Array.isArray(backup.sessions)) {
    for (const session of backup.sessions) {
      const existing = await db.get('sessions', session.id);
      let targetId = session.id;

      if (existing) {
        // If an existing session with identical ID exists, assign a fresh ID to avoid overwriting
        targetId = `${session.id}_imp_${Date.now()}`;
        sessionIdMap.set(session.id, targetId);
      } else {
        sessionIdMap.set(session.id, session.id);
      }

      const sessionToStore: RadarSession = {
        ...session,
        id: targetId,
        active: false // Keep current active session unaffected
      };

      await db.put('sessions', sessionToStore);
      importedSessions++;
      lastSessionId = targetId;
    }
  }

  // 2. Restore and Merge Nodes
  let importedNodes = 0;
  if (Array.isArray(backup.nodes)) {
    for (const node of backup.nodes) {
      const existing = await db.get('nodes', node.num);
      if (existing) {
        // Merge node details, preferring newer lastHeard and existing coordinates if missing
        const merged: StoredNode = {
          ...existing,
          ...node,
          lastHeard: Math.max(existing.lastHeard || 0, node.lastHeard || 0),
          lat: node.lat != null ? node.lat : existing.lat,
          lon: node.lon != null ? node.lon : existing.lon,
          shortName: node.shortName || existing.shortName,
          longName: node.longName || existing.longName
        };
        await db.put('nodes', merged);
      } else {
        await db.put('nodes', node);
      }
      importedNodes++;
    }
  }

  // 3. Restore Packets
  let importedPackets = 0;
  if (Array.isArray(backup.packets)) {
    const tx = db.transaction('packets', 'readwrite');
    for (const packet of backup.packets) {
      const remappedSessionId = sessionIdMap.get(packet.sessionId) || packet.sessionId;
      // Strip old auto-increment key to generate unique primary keys
      const { id, ...packetData } = packet;
      await tx.store.add({
        ...packetData,
        sessionId: remappedSessionId
      });
      importedPackets++;
    }
    await tx.done;
  }

  // 4. Restore Traces if present
  let importedTraces = 0;
  if (Array.isArray(backup.traces)) {
    for (const trace of backup.traces) {
      const remappedSessionId = sessionIdMap.get(trace.sessionId) || trace.sessionId;
      await db.put('traces', {
        ...trace,
        sessionId: remappedSessionId
      });
      importedTraces++;
    }
  }

  return {
    sessionsCount: importedSessions,
    packetsCount: importedPackets,
    nodesCount: importedNodes,
    tracesCount: importedTraces,
    activeSessionId: lastSessionId
  };
}
